import { FC, useEffect, useState } from 'react';
import Spacing from 'components/Spacing';
import { Body, BodySmall } from 'components/Typography';
import { TextFieldWithFloatingLabel } from 'components/FinalForm/TextFieldWithFloatingLabel';
import { USER_FIELD_MAX_LENGTHS } from 'constants/userFieldMaxLengths';
import theme from 'config/theme';
import { Checkbox, CheckboxRow, DetailsColumn, DetailsRow } from '../../styled';
import { useField, useForm } from 'react-final-form';
import Icon, { IconNames } from 'components/Icon';
import {
  RETIRED_OR_UNEMPLOYED,
  getIsRetiredOrUnemployed,
  required,
  EMPLOYMENT_FIELD_NAMES,
} from 'helpers/utils';
import { CountrySelectField, StateSelectField } from 'components/FinalForm/AddressSelectFields';

interface Props {
  isEmployerAddressRequired: boolean;
}

export const EmploymentDetails: FC<Props> = ({ isEmployerAddressRequired }) => {
  const { change: changeForm, getState } = useForm();
  const { employer, occupation } = getState().initialValues;
  const [isRetired, setIsRetired] = useState(getIsRetiredOrUnemployed(employer, occupation));
  const {
    input: { value: employerCountry },
  } = useField('employer_country', { subscription: { value: true } });
  const showEmployerAddressFields = isEmployerAddressRequired;

  useEffect(() => {
    if (isRetired) {
      EMPLOYMENT_FIELD_NAMES.forEach(fieldName => {
        changeForm(fieldName, null);
      });
      changeForm('employer', RETIRED_OR_UNEMPLOYED);
      changeForm('occupation', RETIRED_OR_UNEMPLOYED);
    }
  }, [isRetired]);

  useEffect(() => {
    setIsRetired(getIsRetiredOrUnemployed(employer, occupation));
  }, [employer, occupation]);

  const onCheckboxClick = () => {
    if (isRetired) {
      changeForm('employer', '');
      changeForm('occupation', '');
    }
    setIsRetired(isRetired => !isRetired);
  };

  return (
    <>
      <Body>Employment</Body>
      {!isRetired && (
        <DetailsColumn>
          <DetailsRow>
            <TextFieldWithFloatingLabel
              name="occupation"
              label="Occupation"
              validate={required}
              maxLength={USER_FIELD_MAX_LENGTHS.occupation}
            />
            <TextFieldWithFloatingLabel
              name="employer"
              label="Employer"
              validate={required}
              maxLength={USER_FIELD_MAX_LENGTHS.employer}
            />
          </DetailsRow>
        </DetailsColumn>
      )}
      {showEmployerAddressFields && !isRetired && (
        <DetailsColumn>
          <TextFieldWithFloatingLabel
            name="employer_address1"
            label="Employer Address 1"
            validate={required}
            maxLength={USER_FIELD_MAX_LENGTHS.employer_address1}
          />
          <TextFieldWithFloatingLabel
            name="employer_address2"
            label="Employer Address 2"
            maxLength={USER_FIELD_MAX_LENGTHS.employer_address2}
          />
          <DetailsRow>
            <TextFieldWithFloatingLabel
              name="employer_city"
              label="Employer City"
              validate={required}
              maxLength={USER_FIELD_MAX_LENGTHS.employer_city}
            />
            <StateSelectField
              country={employerCountry}
              name="employer_state"
              label="Employer State"
              labelRegion="Employer Region"
              validate={required}
            />
          </DetailsRow>
          <DetailsRow>
            <TextFieldWithFloatingLabel
              name="employer_zipcode"
              label="Employer Zip"
              validate={required}
              maxLength={USER_FIELD_MAX_LENGTHS.employer_zipcode}
            />
            <CountrySelectField label="Employer Country" name="employer_country" />
          </DetailsRow>
        </DetailsColumn>
      )}
      <Spacing $size={!isRetired ? 10 : 4} />
      <label style={{ display: 'contents' }}>
        <CheckboxRow $alignItems="center" $pressable={true}>
          <input
            type="checkbox"
            className="visually-hidden-checkbox"
            checked={isRetired}
            onChange={onCheckboxClick}
          />
          <Checkbox
            aria-hidden="true"
            $isChecked={isRetired}
            $justifyContent="space-around"
            $alignItems="center"
          >
            {isRetired && (
              <Icon
                name={IconNames.Checkmark}
                color={theme.colors.white}
                size={10}
                aria-hidden="true"
              />
            )}
          </Checkbox>
          <BodySmall $color={theme.colors.shaft}>I'm retired or currently unemployed</BodySmall>
        </CheckboxRow>
      </label>
    </>
  );
};
